/**
 * Plain-language help for Node controls.
 *
 * Operation copy comes from the shared catalogue (ports and labels) or from
 * maintained family text for nodes that are not operations. Model copy is the
 * registry description only — a model name is never turned into a capability.
 */
import {
  WEB_NODES_WITHOUT_OPERATION,
  getSubPurposeLabel,
  graphNodeDef,
  webNodeOperation,
} from '@genieartinc/schemas'

export const NODE_DESCRIPTION_UNAVAILABLE = 'No verified description for this node.'
export const MODEL_DESCRIPTION_UNAVAILABLE = 'No verified description for this model.'
export const HOSTED_TOOL_INPUT_LIMIT = 'This catalogue entry does not list a narrower input set.'

/** Canvas families that open Node controls. ThreeDNode stays gated by its feature flag. */
export const NODE_CONTROL_FAMILIES = [
  'FrameInput',
  'FrameOutput',
  'TextFile',
  'ImageFile',
  'VideoFile',
  'AudioFile',
  'TextNode',
  'ImageNode',
  'VideoNode',
  'ThreeDNode',
  'CommentNode',
  'MaskNode',
  'DataTable',
  'VariationGenerator',
  'PromptComposer',
  'BatchGenerator',
  'GalleryGroup',
  'GalleryContainer',
  'LoraModel',
  'ShaderNode',
] as const

const FAMILY = new Set<string>(NODE_CONTROL_FAMILIES)

/** Nodes whose panel must not load or save a model. */
const MODEL_FREE = new Set<string>([
  'TextFile',
  'ImageFile',
  'VideoFile',
  'AudioFile',
  'CommentNode',
  'MaskNode',
  'DataTable',
  'VariationGenerator',
  'PromptComposer',
  'BatchGenerator',
  'GalleryGroup',
  'GalleryContainer',
  'LoraModel',
  'ShaderNode',
  'FrameInput',
  'FrameOutput',
])

const AI_COMPONENTS: Record<string, string> = {
  ImageNode: 'image',
  VideoNode: 'video',
  TextNode: 'text-llm',
  ThreeDNode: '3d',
}

const AI_NODE_TYPES = new Set(['image', 'video', 'text-llm', '3d', 'sound'])

/** Maintained from the node handle contract, not from a model name. */
const FAMILY_HELP: Record<string, string> = {
  TextFile: 'Holds text you type or paste. It does not run a model. Connect the text out to a prompt or another node.',
  ImageFile: 'Holds an image file on the canvas. It does not run a model. Connect the image out as an input to another node.',
  VideoFile: 'Holds a video file. It does not run a model. Connect the video out to a node that accepts video.',
  AudioFile: 'Holds an audio file. It does not run a model. Connect the audio out to a node that accepts audio.',
  CommentNode: 'A note on the canvas for yourself. It does not run a model and has no inputs or outputs.',
  DataTable: 'A table of values for a series. It does not run a model. Connect its data out to a Variation Generator.',
  VariationGenerator: 'Builds prompt variations from a connected data table. It does not run a model. Connect a Data Table in, and send the variations out.',
  PromptComposer: 'Combines incoming variation prompts into one prompt. It does not run a model. Connect a Variation Generator in, and send the prompt out.',
  BatchGenerator: 'Runs the connected generator once for each variation. Connect a prompt source in. Results land as new files on the canvas.',
  GalleryGroup: 'Groups items on the canvas. It does not run a model.',
  GalleryContainer: 'Holds a set of generated files together. It does not run a model.',
  LoraModel: 'A style adapter you connect into a generation node. It does not generate on its own. Connect its LoRA out to a node that accepts a LoRA.',
  ShaderNode: 'A live shader. Connect the image, video, or audio inputs it declares. The result is an image.',
  MaskNode: 'Paint a mask on a connected image. The mask is an input to another operation, not a finished picture.',
}

export interface NodeControlModelInfo {
  /** Identity only. Never used to invent a capability sentence. */
  name?: string
  /** Registry description. Empty or missing means the metadata is unavailable. */
  description?: string | null
}

export interface NodeControlHelpInput {
  componentType?: string
  nodeType?: string
  purpose?: string
  subPurpose?: string
  model?: NodeControlModelInfo | null
}

export interface NodeControlHelp {
  operation: string
  /** Verified model text, the unavailable sentence, or null when no model is selected. */
  model: string | null
}

export function isNodeControlFamily(componentType: string | undefined): boolean {
  return componentType !== undefined && FAMILY.has(componentType)
}

export function isModelFreeNodeControl(componentType: string | undefined): boolean {
  return componentType !== undefined && MODEL_FREE.has(componentType)
}

export function nodeControlHelp(input: NodeControlHelpInput): NodeControlHelp {
  return {
    operation: describeOperation(input),
    model: input.model ? verifiedModelText(input.model) : null,
  }
}

function describeOperation(input: NodeControlHelpInput): string {
  const component = input.componentType
  if (component && FAMILY_HELP[component]) return FAMILY_HELP[component]

  const purpose = input.purpose ?? (isAiSelection(component, input.nodeType) ? 'create' : undefined)
  if (component) {
    const fromComponent = describeMapped(component, purpose, input.subPurpose)
    if (fromComponent) return fromComponent
  }
  if (input.nodeType && input.nodeType !== component) {
    const fromType = describeMapped(input.nodeType, purpose, input.subPurpose)
    if (fromType) return fromType
  }
  return NODE_DESCRIPTION_UNAVAILABLE
}

function isAiSelection(componentType: string | undefined, nodeType: string | undefined): boolean {
  if (componentType && AI_COMPONENTS[componentType]) return true
  return nodeType !== undefined && AI_NODE_TYPES.has(nodeType)
}

function describeMapped(nodeType: string, purpose?: string, subPurpose?: string): string | null {
  const without = withoutOperationReason(nodeType, purpose, subPurpose)
  if (without) {
    const name = subPurpose ? getSubPurposeLabel(subPurpose) : 'This node'
    return `${name}. ${without}`
  }
  const mapped = webNodeOperation(nodeType, purpose, subPurpose)
  if (!mapped) return null
  if (mapped.operation === 'tool.run') return describeHostedTool(subPurpose)
  const def = graphNodeDef(mapped.operation)
  if (!def) return null
  return formatDef(LEAD[mapped.operation] ?? def.label, def.inputs, def.outputs)
}

const LEAD: Record<string, string> = {
  'canvas.in': 'Brings a chosen Artboard frame into the workflow',
  'canvas.out': 'Writes an image back onto the Artboard',
}

function withoutOperationReason(nodeType: string, purpose?: string, subPurpose?: string): string | undefined {
  if (!purpose || !subPurpose) return undefined
  const type = AI_COMPONENTS[nodeType] ?? nodeType
  const key = `${type}/${purpose}/${subPurpose}`
  return Object.hasOwn(WEB_NODES_WITHOUT_OPERATION, key) ? WEB_NODES_WITHOUT_OPERATION[key] : undefined
}

function describeHostedTool(subPurpose?: string): string {
  const name = subPurpose ? getSubPurposeLabel(subPurpose) : 'Model tool'
  return `${name}. This runs as a hosted model tool. ${HOSTED_TOOL_INPUT_LIMIT} Connect only inputs that tool accepts. The result follows that tool.`
}

function formatDef(
  label: string,
  inputs: ReadonlyArray<{ id: string; type: string }>,
  outputs: ReadonlyArray<{ id: string; type: string }>,
): string {
  const inputSentence = inputs.length === 0
    ? 'Nothing needs to be connected in.'
    : `Connect ${join(inputs.map(inputPhrase))}.`
  const outputSentence = outputs.length === 0
    ? 'It does not return a connected result.'
    : `The result is ${join(outputs.map(outputPhrase))}.`
  return `${label}. ${inputSentence} ${outputSentence}`
}

function inputPhrase(port: { id: string; type: string }): string {
  if (port.id === 'ref') return `a reference ${noun(port.type)}`
  if (port.id === 'control') return `a control ${noun(port.type)}`
  if (port.id === 'mask') return 'a mask'
  if (port.id === 'a') return `a base ${noun(port.type)}`
  if (port.id === 'b') return `an overlay ${noun(port.type)}`
  if (port.type === 'text') return 'text'
  if (port.type === 'video') return 'a video'
  if (port.type === 'audio') return 'audio'
  if (port.type === 'mesh') return 'a 3D mesh'
  if (port.type === 'image') return 'an image'
  return `a ${port.type} input`
}

function outputPhrase(port: { type: string }): string {
  if (port.type === 'text') return 'text'
  if (port.type === 'video') return 'a video'
  if (port.type === 'audio') return 'audio'
  if (port.type === 'mesh') return 'a 3D mesh'
  if (port.type === 'image') return 'an image'
  return `a ${port.type} result`
}

function noun(type: string): string {
  if (type === 'image') return 'image'
  if (type === 'video') return 'video'
  if (type === 'audio') return 'audio'
  if (type === 'mesh') return 'mesh'
  if (type === 'text') return 'text'
  return type
}

function join(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

function verifiedModelText(model: NodeControlModelInfo): string {
  const description = typeof model.description === 'string' ? model.description.trim() : ''
  // name is intentionally unused.
  return description || MODEL_DESCRIPTION_UNAVAILABLE
}
